import React from "react";
import NavigationHeader from "@/components/NavigationHeader";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import HomeAuroraBackground from "@/components/HomeAuroraBackground";
import trustedMark from "@/assets/Trusted.webp";
import { motion } from "framer-motion";
import {
  FileText,
  Server,
  Users,
  AlertCircle,
  Database,
  Shield,
  RefreshCw,
  Stethoscope,
  AlertTriangle,
  XCircle,
  Scale,
  CheckCircle,
  Check,
  ChevronRight,
} from "lucide-react";

const Terms = () => {
  const sections = [
    {
      icon: FileText,
      title: "1. Introduction",
      content:
        "These Terms and Conditions (“Terms”) govern the use of Vet Assist, a veterinary practice management system developed and operated by Endsofttech Web Solutions (“Service Provider”). By accessing or using the platform, you (“Client”) agree to be bound by these Terms.",
    },
    {
      icon: Server,
      title: "2. Scope of Services",
      content:
        "Vet Assist provides a cloud-based veterinary practice management platform including, but not limited to:",
      list: [
        "Appointment and schedule management",
        "Electronic medical records (EMR)",
        "Billing and invoicing",
        "Analytics and reporting",
        "Client communication tools",
        "Optional clinic website (subject to plan inclusions)",
      ],
      footer: "Feature availability may vary depending on the subscribed plan.",
    },
    {
      icon: Users,
      title: "3. Authorized Use",
      list: [
        "Only authorized clinic personnel may access and use the system. Account sharing outside the registered clinic is strictly prohibited.",
        "The Client is responsible for all activities performed under their account.",
      ],
    },
    {
      icon: AlertCircle,
      title: "4. Acceptable Use Policy",
      content:
        "The Client agrees to use Vet Assist responsibly and legally. The following are strictly prohibited:",
      list: [
        "Abuse of SMS, email, or notification systems",
        "Unauthorized automation or system overload",
        "Use of offensive, profane, or inappropriate language in any system labels or records",
        "Attempting to access source code, reverse engineer, resell, or tamper with the platform",
      ],
      footer: "Violation may result in suspension or termination without refund.",
    },
    {
      icon: Database,
      title: "5. Data Ownership & Intellectual Property",
      list: [
        "All clinic, patient, and operational data entered into the system remain the sole property of the Client.",
        "Vet Assist, its software, branding, and underlying technology remain the exclusive intellectual property of Endsofttech Web Solutions.",
      ],
    },
    {
      icon: Shield,
      title: "6. Data Privacy Compliance",
      content:
        "Both parties agree to comply with the Data Privacy Act of 2012 (RA 10173). Personal and sensitive data shall be processed lawfully, securely, and only for legitimate business purposes.",
    },
    {
      icon: RefreshCw,
      title: "7. System Updates & Modifications",
      content:
        "The Service Provider may release updates, enhancements, or patches to improve performance or security. Scheduled maintenance will be communicated in advance whenever possible.",
    },
    {
      icon: Stethoscope,
      title: "8. Medical Disclaimer",
      content:
        "Vet Assist is a support tool only. It does not replace professional veterinary judgment, diagnosis, or treatment decisions. The Service Provider shall not be liable for clinical outcomes.",
    },
    {
      icon: AlertTriangle,
      title: "9. Limitation of Liability",
      content:
        "Endsofttech Web Solutions shall not be liable for indirect, incidental, or consequential damages, including loss of profits or data. Total liability shall not exceed the amount paid by the Client in the last three (3) months of subscription.",
    },
    {
      icon: XCircle,
      title: "10. Termination",
      content: "Access may be suspended or terminated for:",
      list: ["Breach of these Terms", "Non-payment", "Illegal or abusive use of the platform"],
      footer: "Upon termination, data may be exported upon request, subject to account clearance.",
    },
    {
      icon: Scale,
      title: "11. Governing Law",
      content:
        "These Terms shall be governed by the laws of the Republic of the Philippines. Disputes shall be settled in the courts of Las Piñas City.",
    },
    {
      icon: CheckCircle,
      title: "12. Acceptance",
      content:
        "By using Vet Assist, the Client acknowledges and agrees to these Terms and Conditions.",
    },
  ];

  return (
    <>
      <SEO page="terms" />
      <div
        className="relative w-full font-sans text-slate-900 selection:bg-blue-500/30 selection:text-blue-900"
        style={{
          background: "linear-gradient(135deg, #dbeafe 0%, #e8f4ff 20%, #f0fdf4 60%, #dcfce7 100%)",
        }}>
        <HomeAuroraBackground
          showStars={false}
          showWavingLines={true}
          className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
        />

        <NavigationHeader />

        {/* Hero Section */}
        <section className="relative px-6 pt-40 pb-16">
          <div className="mx-auto max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8 inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/5 px-4 py-1.5 shadow-sm backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500"></span>
              </span>
              <span className="text-xs font-semibold tracking-wide text-blue-800 uppercase">
                Official Agreement
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-6 text-5xl leading-[1.1] font-extrabold tracking-tight text-[#1A3A5C] md:text-7xl">
              Terms &{" "}
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Conditions
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mx-auto max-w-2xl text-xl leading-relaxed text-slate-700">
              Please read these terms carefully before using our platform to ensure a clear
              understanding of our services.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.28 }}
              className="mt-8 flex justify-center">
              <div className="rounded-2xl border border-blue-100/70 bg-white/80 p-4 shadow-sm backdrop-blur-sm">
                <img
                  src={trustedMark}
                  alt="Trusted service mark"
                  className="h-auto w-40 object-contain sm:w-48"
                  loading="lazy"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-6 text-sm font-medium text-slate-600">
              Last updated: February 2026
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <div className="mx-auto max-w-5xl px-4 pb-12 md:px-8">
          <div className="grid gap-6">
            {sections.map((section, index) => {
              const Icon = section.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="group relative rounded-2xl border border-gray-100 bg-white/70 p-8 shadow-sm backdrop-blur-md transition-all duration-300 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-500/5">
                  <div className="flex flex-col gap-6 md:flex-row">
                    {/* Icon Container */}
                    <div className="flex-shrink-0">
                      <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-blue-50 bg-gradient-to-br from-blue-50 to-white text-blue-600 shadow-sm transition-all duration-300 group-hover:scale-105 group-hover:bg-blue-600 group-hover:bg-none group-hover:text-white">
                        <Icon className="h-7 w-7" strokeWidth={1.5} />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 pt-1">
                      <h2 className="mb-4 flex items-center gap-2 text-xl font-bold text-gray-900 md:text-2xl">
                        {section.title}
                      </h2>

                      {section.content && (
                        <p className="mb-6 leading-relaxed text-gray-600">{section.content}</p>
                      )}

                      {section.list && (
                        <ul className="mb-4 grid gap-3">
                          {section.list.map((item, i) => (
                            <li key={i} className="group/item flex items-start gap-3">
                              <ChevronRight className="mt-1 h-4 w-4 text-blue-400 transition-colors group-hover/item:text-blue-600" />
                              <span className="leading-relaxed text-gray-600">{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {section.footer && (
                        <div className="mt-6 border-t border-gray-100/50 pt-4">
                          <p className="flex items-center gap-2 text-sm text-gray-400 italic">
                            <AlertCircle className="h-4 w-4" />
                            {section.footer}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default Terms;
